/**
 * AnteprimaFatturaLive — replica HTML/React del PDF fattura con 7 template distinti.
 * Aggiornamento istantaneo al variare del layout senza round-trip backend.
 */
import React, { useMemo, memo } from "react";

// ── dati di esempio ──────────────────────────────────────────────
const SAMPLE_VOCI = [
  { descrizione: "Soggiorno mese di Maggio 2026", quantita: 1, prezzo_unitario: 1670.00, regime_iva: "esente_art10" },
  { descrizione: "Farmaci e materiale sanitario", quantita: 1, prezzo_unitario: 25.50,   regime_iva: "esclusa_art15" },
  { descrizione: "Servizio blisteraggio",         quantita: 1, prezzo_unitario: 30.00,   regime_iva: "iva_22" },
];

const SAMPLE_DEST = {
  nome: "ROSSI MARIO",
  cf: "RSSMRA80A01H501Z",
  email: "mario.rossi@example.com",
  indirizzo: "VIA ROMA 1, 00100 ROMA (RM)",
};

const IVA_PERC = {
  iva_4: "4%", iva_10: "10%", iva_22: "22%",
  esente_art10: "Es.", esclusa_art15: "Escl.", ni_art2: "N.I.",
  fuori_campo: "F.C.", iva_0: "0%",
};

const IVA_COL_NUM = {
  iva_4: 4, iva_10: 10, iva_22: 22,
  esente_art10: 0, esclusa_art15: 0, ni_art2: 0, fuori_campo: 0, iva_0: 0,
};

const IVA_RIEPILOGO_LABEL = {
  esente_art10: "0% - Esente Art. 10 DPR 633/72",
  esclusa_art15: "0% - Escluso Art. 15 DPR 633/72",
  iva_22: "22%",
  iva_10: "10%",
  iva_4: "4%",
  ni_art2: "0% - N.I.",
  fuori_campo: "0% - Fuori campo",
  iva_0: "0%",
};

// ── helpers ──────────────────────────────────────────────────────
function fmt(v) {
  return Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcTotali(voci) {
  const imp = voci.reduce((s, v) => s + (v.quantita || 1) * (v.prezzo_unitario || 0), 0);
  const iva = voci.reduce((s, v) => {
    const base = (v.quantita || 1) * (v.prezzo_unitario || 0);
    const r = v.regime_iva || "";
    const p = r === "iva_22" ? 0.22 : r === "iva_10" ? 0.10 : r === "iva_4" ? 0.04 : 0;
    return s + base * p;
  }, 0);
  return {
    ti: Math.round(imp * 100) / 100,
    tv: Math.round(iva * 100) / 100,
    tt: Math.round((imp + iva) * 100) / 100,
  };
}

function groupIvaDetailed(voci) {
  const g = {};
  voci.forEach(v => {
    const r = v.regime_iva || "iva_22";
    const base = (v.quantita || 1) * (v.prezzo_unitario || 0);
    const p = r === "iva_22" ? 0.22 : r === "iva_10" ? 0.10 : r === "iva_4" ? 0.04 : 0;
    if (!g[r]) g[r] = { imp: 0, iva: 0 };
    g[r].imp += base;
    g[r].iva += base * p;
  });
  return Object.entries(g).map(([r, d]) => ({
    regime: IVA_RIEPILOGO_LABEL[r] || IVA_PERC[r] || r,
    imp: d.imp,
    iva: d.iva,
  }));
}

function splitImponibile(voci) {
  let imponibile = 0;
  let nonImponibile = 0;
  voci.forEach(v => {
    const base = (v.quantita || 1) * (v.prezzo_unitario || 0);
    const r = v.regime_iva || "";
    if (r === "esclusa_art15" || r === "fuori_campo") nonImponibile += base;
    else imponibile += base;
  });
  return { imponibile, nonImponibile };
}

function groupIva(voci) {
  const g = {};
  voci.forEach(v => {
    const r = v.regime_iva || "iva_22";
    const base = (v.quantita || 1) * (v.prezzo_unitario || 0);
    const p = r === "iva_22" ? 0.22 : r === "iva_10" ? 0.10 : r === "iva_4" ? 0.04 : 0;
    if (!g[r]) g[r] = { imp: 0, iva: 0 };
    g[r].imp += base;
    g[r].iva += base * p;
  });
  return Object.entries(g).map(([r, d]) => ({ regime: IVA_PERC[r] || r, imp: d.imp, iva: d.iva }));
}

function LogoPlaceholder({ acc, w = 80, h = 36, fontSize = 10 }) {
  return (
    <div style={{
      width: w, height: h, background: "#f3f4f6", borderRadius: 4,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#9ca3af", fontSize, border: "1.5px dashed #d1d5db", flexShrink: 0,
    }}>LOGO</div>
  );
}

const LOGO_WIDTH_DEFAULT_CM = 4.5;

function logoScale(lyt) {
  const cm = Number(lyt.logo_larghezza_cm) || LOGO_WIDTH_DEFAULT_CM;
  return Math.max(2, Math.min(8, cm)) / LOGO_WIDTH_DEFAULT_CM;
}

function logoSize(lyt, baseW, baseH) {
  const s = logoScale(lyt);
  return { w: Math.round(baseW * s), h: Math.round(baseH * s) };
}

function LogoImg({ lyt, w = 80, h = 40, style }) {
  if (!lyt.blocco_logo) return null;
  const sz = logoSize(lyt, w, h);
  if (lyt.logo_base64) {
    return (
      <img src={`data:${lyt.logo_content_type || "image/png"};base64,${lyt.logo_base64}`}
        alt="logo" style={{ maxWidth: sz.w, maxHeight: sz.h, objectFit: "contain", ...style }} />
    );
  }
  return <LogoPlaceholder w={sz.w} h={sz.h} />;
}

// ────────────────────────────────────────────────────────────────
// CLASSICO
// Logo sx · dati struttura dx · titolo con doppia riga accento
// Intestazione bicolonna · tabella bordi completi · footer testo
// ────────────────────────────────────────────────────────────────
function TemplateClassico({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { ti, tv, tt } = calcTotali(voci);
  const rp = dense ? "5px 8px" : "9px 8px";

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <LogoImg lyt={lyt} w={88} h={44} />
        <div style={{ textAlign: "right", fontSize: 11 }}>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", fontSize: 13, color: "#111827" }}>{rs}</div>}
          {lyt.mostra_indirizzo && addr && <div style={{ color: "#4b5563" }}>{addr}</div>}
          {lyt.mostra_piva && pvLine && <div style={{ color: "#4b5563" }}>{pvLine}</div>}
          {ext && lyt.mostra_telefono && telefono && <div style={{ color: "#6b7280" }}>Tel. {telefono}</div>}
          {ext && lyt.mostra_email && email && <div style={{ color: "#6b7280" }}>{email}</div>}
          {ext && lyt.mostra_sito_web && sito && <div style={{ color: "#6b7280" }}>{sito}</div>}
        </div>
      </div>

      {/* Titolo */}
      <div style={{ borderTop: `2px solid ${acc}`, borderBottom: `2px solid ${acc}`, textAlign: "center", padding: "7px 0", marginBottom: 14, fontWeight: "bold", fontSize: 13, letterSpacing: "0.5px" }}>
        ANTEPRIMA FATTURA — MAGGIO 2026
      </div>

      {/* Mittente / Destinatario */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, fontSize: 10.5, color: "#374151" }}>
          <div style={{ fontSize: 8, color: "#9ca3af", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>Cedente</div>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold" }}>{rs}</div>}
          {lyt.mostra_piva && pvLine && <div>{pvLine}</div>}
          {lyt.mostra_indirizzo && addr && <div>{addr}</div>}
        </div>
        <div style={{ flex: 1, textAlign: "right", fontSize: 10.5 }}>
          <div style={{ fontSize: 8, color: "#9ca3af", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>Destinatario</div>
          <div style={{ fontWeight: "bold", color: "#111827" }}>{dest.nome}</div>
          {dest.cf && <div style={{ color: "#4b5563" }}>C.F. {dest.cf}</div>}
          {dest.indirizzo && <div style={{ color: "#4b5563" }}>{dest.indirizzo}</div>}
          {dest.email && <div style={{ color: "#6b7280" }}>{dest.email.toLowerCase()}</div>}
        </div>
      </div>

      {/* Tabella — bordi completi stile registro */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, fontSize: 11, border: `1px solid ${acc}` }}>
        <thead>
          <tr style={{ background: acc, color: "#fff" }}>
            <th style={{ padding: "8px 8px", textAlign: "left", border: `1px solid ${acc}` }}>Descrizione</th>
            <th style={{ padding: "8px 6px", textAlign: "center", width: "7%", border: `1px solid ${acc}` }}>Qtà</th>
            <th style={{ padding: "8px 8px", textAlign: "right", width: "17%", border: `1px solid ${acc}` }}>Prezzo unit.</th>
            <th style={{ padding: "8px 8px", textAlign: "right", width: "17%", border: `1px solid ${acc}` }}>Importo</th>
            <th style={{ padding: "8px 6px", textAlign: "center", width: "9%", border: `1px solid ${acc}` }}>IVA</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
              <td style={{ padding: rp, border: "1px solid #e5e7eb" }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "center", border: "1px solid #e5e7eb" }}>{fmt(v.quantita || 1)}</td>
              <td style={{ padding: rp, textAlign: "right", border: "1px solid #e5e7eb" }}>€ {fmt(v.prezzo_unitario || 0)}</td>
              <td style={{ padding: rp, textAlign: "right", border: "1px solid #e5e7eb" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center", border: "1px solid #e5e7eb" }}>{IVA_PERC[v.regime_iva] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <BlocchiComuni lyt={lyt} iban={iban} tt={tt} acc={acc} />

      {/* Riepilogo IVA + totali */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 8 }}>
        <IvaTable lyt={lyt} voci={voci} acc={acc} />
        <TotaliBlock ti={ti} tv={tv} tt={tt} acc={acc} />
      </div>

      <FooterDoc lyt={lyt} rs={rs} email={email} acc={acc} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// MODERNO
// Banner header con barra sinistra colorata · titolo pill arrotondato
// Destinatario in card shaded · tabella senza bordi laterali · totale accent
// ────────────────────────────────────────────────────────────────
function TemplateModerno({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { ti, tv, tt } = calcTotali(voci);
  const rp = dense ? "6px 10px" : "11px 10px";

  return (
    <>
      {/* Header card con barra sinistra */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", borderLeft: `5px solid ${acc}`, borderRadius: "0 8px 8px 0", padding: "12px 14px", marginBottom: 16 }}>
        <LogoImg lyt={lyt} w={90} h={44} />
        <div style={{ textAlign: "right", fontSize: 11 }}>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", fontSize: 14, color: acc }}>{rs}</div>}
          {lyt.mostra_indirizzo && addr && <div style={{ color: "#4b5563" }}>{addr}</div>}
          {lyt.mostra_piva && pvLine && <div style={{ color: "#6b7280" }}>{pvLine}</div>}
          {ext && lyt.mostra_email && email && <div style={{ color: "#6b7280" }}>{email}</div>}
          {ext && lyt.mostra_telefono && telefono && <div style={{ color: "#6b7280" }}>Tel. {telefono}</div>}
          {ext && lyt.mostra_sito_web && sito && <div style={{ color: "#6b7280" }}>{sito}</div>}
        </div>
      </div>

      {/* Titolo pill */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <div style={{ background: acc, color: "#fff", borderRadius: 20, padding: "6px 24px", fontWeight: "bold", fontSize: 13, letterSpacing: "0.5px" }}>
          ANTEPRIMA FATTURA — MAGGIO 2026
        </div>
      </div>

      {/* Destinatario in card */}
      <div style={{ background: "#f8fafc", border: `1px solid #e5e7eb`, borderLeft: `4px solid ${acc}`, borderRadius: 6, padding: "10px 14px", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 8.5, color: "#9ca3af", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.5px" }}>Da</div>
            {lyt.mostra_nome_struttura && <div style={{ fontWeight: "600", color: "#111827" }}>{rs}</div>}
            {lyt.mostra_piva && pvLine && <div style={{ color: "#6b7280", fontSize: 10.5 }}>{pvLine}</div>}
            {lyt.mostra_indirizzo && addr && <div style={{ color: "#6b7280", fontSize: 10.5 }}>{addr}</div>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 8.5, color: "#9ca3af", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.5px" }}>A</div>
            <div style={{ fontWeight: "600", color: "#111827" }}>{dest.nome}</div>
            {dest.cf && <div style={{ color: "#6b7280", fontSize: 10.5 }}>C.F. {dest.cf}</div>}
            {dest.indirizzo && <div style={{ color: "#6b7280", fontSize: 10.5 }}>{dest.indirizzo}</div>}
          </div>
        </div>
      </div>

      {/* Tabella senza bordi laterali, solo bottom */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, fontSize: 11.5 }}>
        <thead>
          <tr style={{ borderBottom: `3px solid ${acc}` }}>
            <th style={{ padding: "10px 10px 8px", textAlign: "left", color: acc, fontWeight: "700", fontSize: 10.5 }}>DESCRIZIONE</th>
            <th style={{ padding: "10px 8px 8px", textAlign: "center", width: "7%", color: acc, fontWeight: "700", fontSize: 10.5 }}>QTÀ</th>
            <th style={{ padding: "10px 10px 8px", textAlign: "right", width: "17%", color: acc, fontWeight: "700", fontSize: 10.5 }}>PREZZO</th>
            <th style={{ padding: "10px 10px 8px", textAlign: "right", width: "17%", color: acc, fontWeight: "700", fontSize: 10.5 }}>IMPORTO</th>
            <th style={{ padding: "10px 8px 8px", textAlign: "center", width: "9%", color: acc, fontWeight: "700", fontSize: 10.5 }}>IVA</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #e5e7eb", background: i % 2 === 1 ? "#f8fafc" : "#fff" }}>
              <td style={{ padding: rp }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "center" }}>{fmt(v.quantita || 1)}</td>
              <td style={{ padding: rp, textAlign: "right" }}>€ {fmt(v.prezzo_unitario || 0)}</td>
              <td style={{ padding: rp, textAlign: "right" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center" }}>{IVA_PERC[v.regime_iva] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <BlocchiComuni lyt={lyt} iban={iban} tt={tt} acc={acc} />

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 8 }}>
        <IvaTable lyt={lyt} voci={voci} acc={acc} />
        {/* Totali in card accent */}
        <div style={{ flex: "0 0 38%", background: "#f8fafc", borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6b7280", marginBottom: 5 }}>
            <span>Imponibile</span><span>€ {fmt(ti)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
            <span>IVA</span><span>€ {fmt(tv)}</span>
          </div>
          <div style={{ background: acc, borderRadius: 6, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#fff", fontSize: 11, fontWeight: "600" }}>TOTALE</span>
            <span style={{ color: "#fff", fontWeight: "bold", fontSize: 18 }}>€ {fmt(tt)}</span>
          </div>
        </div>
      </div>

      <FooterDoc lyt={lyt} rs={rs} email={email} acc={acc} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// SANITARIO
// Banner full-width colorato con logo + dati bianchi · struttura healthcare
// Destinatario in box grigio chiaro · tabella con bordi su tutti i lati
// Dati fiscali enfatizzati
// ────────────────────────────────────────────────────────────────
function TemplateSanitario({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { ti, tv, tt } = calcTotali(voci);
  const rp = dense ? "5px 7px" : "8px 7px";

  return (
    <>
      {/* Banner colorato full-width */}
      <div style={{ background: acc, borderRadius: 6, padding: "14px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", fontSize: 15, color: "#fff" }}>{rs}</div>}
          {lyt.mostra_indirizzo && addr && <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10.5 }}>{addr}</div>}
          {lyt.mostra_piva && pvLine && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>{pvLine}</div>}
          {ext && lyt.mostra_email && email && <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>{email}</div>}
          {ext && lyt.mostra_telefono && telefono && <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>Tel. {telefono}</div>}
        </div>
        {lyt.blocco_logo && (
          lyt.logo_base64
            ? <LogoImg lyt={lyt} w={80} h={40} style={{ filter: "brightness(0) invert(1)", opacity: 0.9 }} />
            : <div style={{ width: 70, height: 34, background: "rgba(255,255,255,0.2)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.7)", fontSize: 9, border: "1.5px dashed rgba(255,255,255,0.4)" }}>LOGO</div>
        )}
      </div>

      {/* Titolo con riga sotto */}
      <div style={{ borderBottom: `3px solid ${acc}`, paddingBottom: 7, marginBottom: 14 }}>
        <div style={{ fontWeight: "bold", fontSize: 14, color: "#111827" }}>ANTEPRIMA FATTURA</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>Periodo: Maggio 2026 &nbsp;·&nbsp; Scadenza: 28/06/2026</div>
      </div>

      {/* Destinatario shaded box */}
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "10px 12px", marginBottom: 14, display: "flex", gap: 20 }}>
        <div style={{ flex: 1, fontSize: 10.5 }}>
          <div style={{ fontSize: 8.5, color: "#6b7280", marginBottom: 3, fontWeight: "600", textTransform: "uppercase" }}>Struttura emittente</div>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", color: "#111827", marginBottom: 2 }}>{rs}</div>}
          {lyt.mostra_piva && pvLine && <div style={{ color: "#374151" }}>{pvLine}</div>}
          {lyt.mostra_indirizzo && addr && <div style={{ color: "#374151" }}>{addr}</div>}
        </div>
        <div style={{ flex: 1, fontSize: 10.5, textAlign: "right" }}>
          <div style={{ fontSize: 8.5, color: "#6b7280", marginBottom: 3, fontWeight: "600", textTransform: "uppercase" }}>Paziente / Destinatario</div>
          <div style={{ fontWeight: "bold", fontSize: 12, color: "#111827" }}>{dest.nome}</div>
          {dest.cf && <div style={{ color: "#374151" }}>C.F. {dest.cf}</div>}
          {dest.indirizzo && <div style={{ color: "#6b7280" }}>{dest.indirizzo}</div>}
        </div>
      </div>

      {/* Tabella con bordi su tutti i lati, stile registro sanitario */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, fontSize: 11, border: "1px solid #d1d5db" }}>
        <thead>
          <tr style={{ background: acc, color: "#fff" }}>
            <th style={{ padding: "7px 8px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.3)" }}>Prestazione / Servizio</th>
            <th style={{ padding: "7px 6px", textAlign: "center", width: "7%", borderRight: "1px solid rgba(255,255,255,0.3)" }}>Qtà</th>
            <th style={{ padding: "7px 8px", textAlign: "right", width: "16%", borderRight: "1px solid rgba(255,255,255,0.3)" }}>Prezzo</th>
            <th style={{ padding: "7px 8px", textAlign: "right", width: "16%", borderRight: "1px solid rgba(255,255,255,0.3)" }}>Importo</th>
            <th style={{ padding: "7px 6px", textAlign: "center", width: "10%" }}>Esenzione</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff", borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: rp, borderRight: "1px solid #e5e7eb" }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "center", borderRight: "1px solid #e5e7eb" }}>{fmt(v.quantita || 1)}</td>
              <td style={{ padding: rp, textAlign: "right", borderRight: "1px solid #e5e7eb" }}>€ {fmt(v.prezzo_unitario || 0)}</td>
              <td style={{ padding: rp, textAlign: "right", borderRight: "1px solid #e5e7eb" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center" }}>{IVA_PERC[v.regime_iva] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <BlocchiComuni lyt={lyt} iban={iban} tt={tt} acc={acc} />

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 8 }}>
        <IvaTable lyt={lyt} voci={voci} acc={acc} />
        <TotaliBlock ti={ti} tv={tv} tt={tt} acc={acc} />
      </div>

      <FooterDoc lyt={lyt} rs={rs} email={email} acc={acc} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// MINIMAL
// Logo + nome centrati · nessun bordo sulla tabella eccetto underline
// Massimo spazio bianco · totale grande a destra senza tabella
// Etichette sezione in uppercase sottile
// ────────────────────────────────────────────────────────────────
function TemplateMinimal({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { ti, tv, tt } = calcTotali(voci);
  const rp = dense ? "5px 0" : "9px 0";

  return (
    <>
      {/* Header centrato */}
      <div style={{ textAlign: "center", marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid #e5e7eb` }}>
        {lyt.blocco_logo && (
          lyt.logo_base64
            ? <LogoImg lyt={lyt} w={100} h={50} style={{ marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
            : <div style={{ width: 80, height: 36, background: "#f3f4f6", borderRadius: 4, margin: "0 auto 8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 9, border: "1px dashed #d1d5db" }}>LOGO</div>
        )}
        {lyt.mostra_nome_struttura && <div style={{ fontWeight: "300", fontSize: 16, letterSpacing: "2px", color: "#111827", textTransform: "uppercase" }}>{rs}</div>}
        <div style={{ marginTop: 4, fontSize: 10, color: "#9ca3af", letterSpacing: "0.5px" }}>
          {[lyt.mostra_piva && pvLine, lyt.mostra_indirizzo && addr].filter(Boolean).join("  ·  ")}
        </div>
      </div>

      {/* Titolo minimal */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 10, color: "#9ca3af", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 2 }}>Documento</div>
        <div style={{ fontWeight: "600", fontSize: 15, color: "#111827" }}>Anteprima Fattura <span style={{ color: acc }}>—</span> Maggio 2026</div>
      </div>

      {/* Destinatario minimal */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid #e5e7eb` }}>
        <div style={{ fontSize: 10.5, color: "#6b7280" }}>
          <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>Destinatario</div>
          <div style={{ fontWeight: "600", color: "#111827", fontSize: 12 }}>{dest.nome}</div>
          {dest.cf && <div>C.F. {dest.cf}</div>}
          {dest.indirizzo && <div>{dest.indirizzo}</div>}
        </div>
        <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "right" }}>
          <div>Scadenza</div>
          <div style={{ color: "#111827", fontWeight: "600", fontSize: 12 }}>28/06/2026</div>
        </div>
      </div>

      {/* Tabella no-border, solo underline */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${acc}` }}>
            <th style={{ padding: "0 0 6px", textAlign: "left", fontWeight: "600", color: "#374151", fontSize: 10 }}>DESCRIZIONE</th>
            <th style={{ padding: "0 0 6px", textAlign: "center", width: "8%", fontWeight: "600", color: "#374151", fontSize: 10 }}>QTÀ</th>
            <th style={{ padding: "0 0 6px", textAlign: "right", width: "18%", fontWeight: "600", color: "#374151", fontSize: 10 }}>PREZZO</th>
            <th style={{ padding: "0 0 6px", textAlign: "right", width: "18%", fontWeight: "600", color: "#374151", fontSize: 10 }}>IMPORTO</th>
            <th style={{ padding: "0 0 6px", textAlign: "center", width: "9%", fontWeight: "600", color: "#374151", fontSize: 10 }}>IVA</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: rp }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "center", color: "#6b7280" }}>{fmt(v.quantita || 1)}</td>
              <td style={{ padding: rp, textAlign: "right", color: "#6b7280" }}>€ {fmt(v.prezzo_unitario || 0)}</td>
              <td style={{ padding: rp, textAlign: "right", color: "#374151" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center", color: "#9ca3af", fontSize: 10 }}>{IVA_PERC[v.regime_iva] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Note inline senza box */}
      {lyt.blocco_note_automatiche && (
        <p style={{ fontSize: 10, color: "#9ca3af", marginBottom: 12, fontStyle: "italic", lineHeight: 1.5 }}>
          {lyt.note_automatiche_text || "Documento di anteprima: non costituisce fattura elettronica valida ai fini fiscali."}
        </p>
      )}

      {/* Totale minimalista */}
      <div style={{ textAlign: "right", paddingTop: 12, borderTop: `1px solid #e5e7eb` }}>
        <div style={{ fontSize: 10.5, color: "#9ca3af", marginBottom: 4 }}>
          Imponibile € {fmt(ti)} &nbsp;+&nbsp; IVA € {fmt(tv)}
        </div>
        <div style={{ fontWeight: "300", fontSize: 28, color: acc, letterSpacing: "-0.5px" }}>€ {fmt(tt)}</div>
        <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: "1px", textTransform: "uppercase", marginTop: 2 }}>Totale dovuto</div>
      </div>

      {/* IVA detail collapsed */}
      {lyt.blocco_riepilogo_iva_dettagliato && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid #f3f4f6", fontSize: 10, color: "#9ca3af" }}>
          {groupIva(voci).map((r, i) => (
            <span key={i}>
              {r.regime}: impon. € {fmt(r.imp)}, imp. € {fmt(r.iva)}
              {i < groupIva(voci).length - 1 ? "  ·  " : ""}
            </span>
          ))}
        </div>
      )}

      <FooterDoc lyt={lyt} rs={rs} email={email} acc={acc} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// PREMIUM
// Header split: colonna sinistra colorata con logo grande + documento info
// Colonna destra: dati struttura su sfondo bianco con bordo sottile
// Tabella elegante: accento colore su header, riga totale colorata
// Totale molto grande, firma/coordinamento ben strutturati
// ────────────────────────────────────────────────────────────────
function TemplatePremium({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { ti, tv, tt } = calcTotali(voci);
  const rp = dense ? "7px 10px" : "13px 10px";

  // Versione chiara del colore (per sfondi)
  const accLight = acc + "18";

  return (
    <>
      {/* Header bipartito */}
      <div style={{ display: "flex", marginBottom: 20, borderRadius: 8, overflow: "hidden", border: `1px solid ${acc}` }}>
        {/* Colonna sx — colorata */}
        <div style={{ background: acc, padding: "18px 16px", width: "42%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {lyt.blocco_logo && (
            lyt.logo_base64
              ? <LogoImg lyt={lyt} w={100} h={48} style={{ filter: "brightness(0) invert(1)", marginBottom: 12 }} />
              : <div style={{ width: 80, height: 36, background: "rgba(255,255,255,0.2)", borderRadius: 4, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.7)", fontSize: 9, border: "1px dashed rgba(255,255,255,0.4)" }}>LOGO</div>
          )}
          <div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 3 }}>Documento</div>
            <div style={{ color: "#fff", fontWeight: "bold", fontSize: 13 }}>ANTEPRIMA FATTURA</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, marginTop: 2 }}>Maggio 2026</div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.7)", fontSize: 10 }}>
              Scadenza: 28/06/2026
            </div>
          </div>
        </div>
        {/* Colonna dx — bianca */}
        <div style={{ flex: 1, padding: "18px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>Emesso da</div>
            {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", fontSize: 14, color: "#111827" }}>{rs}</div>}
            {lyt.mostra_indirizzo && addr && <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 2 }}>{addr}</div>}
            {lyt.mostra_piva && pvLine && <div style={{ fontSize: 10.5, color: "#6b7280" }}>{pvLine}</div>}
            {ext && lyt.mostra_email && email && <div style={{ fontSize: 10, color: "#9ca3af" }}>{email}</div>}
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>Destinatario</div>
            <div style={{ fontWeight: "bold", fontSize: 12, color: "#111827" }}>{dest.nome}</div>
            {dest.cf && <div style={{ fontSize: 10.5, color: "#6b7280" }}>C.F. {dest.cf}</div>}
            {dest.indirizzo && <div style={{ fontSize: 10.5, color: "#6b7280" }}>{dest.indirizzo}</div>}
            {dest.email && <div style={{ fontSize: 10, color: "#9ca3af" }}>{dest.email.toLowerCase()}</div>}
          </div>
        </div>
      </div>

      {/* Tabella premium — header colorato, ultima riga totale */}
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, marginBottom: 14, fontSize: 11.5 }}>
        <thead>
          <tr>
            <th style={{ padding: "10px 10px", textAlign: "left", background: acc, color: "#fff", borderRadius: "6px 0 0 0", fontSize: 10.5 }}>Descrizione</th>
            <th style={{ padding: "10px 8px", textAlign: "center", background: acc, color: "#fff", width: "7%", fontSize: 10.5 }}>Qtà</th>
            <th style={{ padding: "10px 10px", textAlign: "right", background: acc, color: "#fff", width: "17%", fontSize: 10.5 }}>Prezzo</th>
            <th style={{ padding: "10px 10px", textAlign: "right", background: acc, color: "#fff", width: "17%", fontSize: 10.5 }}>Importo</th>
            <th style={{ padding: "10px 8px", textAlign: "center", background: acc, color: "#fff", width: "9%", borderRadius: "0 6px 0 0", fontSize: 10.5 }}>IVA</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? accLight : "#fff" }}>
              <td style={{ padding: rp, borderBottom: "1px solid #f3f4f6" }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "center", borderBottom: "1px solid #f3f4f6", color: "#6b7280" }}>{fmt(v.quantita || 1)}</td>
              <td style={{ padding: rp, textAlign: "right", borderBottom: "1px solid #f3f4f6", color: "#6b7280" }}>€ {fmt(v.prezzo_unitario || 0)}</td>
              <td style={{ padding: rp, textAlign: "right", borderBottom: "1px solid #f3f4f6" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center", borderBottom: "1px solid #f3f4f6", color: "#9ca3af" }}>{IVA_PERC[v.regime_iva] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <BlocchiComuni lyt={lyt} iban={iban} tt={tt} acc={acc} />

      {/* Totali premium */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 8 }}>
        <IvaTable lyt={lyt} voci={voci} acc={acc} />
        <div style={{ flex: "0 0 38%", textAlign: "right" }}>
          <table style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280" }}>
            <tbody>
              <tr><td style={{ paddingBottom: 4, paddingRight: 16 }}>Imponibile</td><td style={{ paddingBottom: 4, textAlign: "right" }}>€ {fmt(ti)}</td></tr>
              <tr><td style={{ paddingBottom: 8, paddingRight: 16 }}>IVA</td><td style={{ paddingBottom: 8, textAlign: "right" }}>€ {fmt(tv)}</td></tr>
            </tbody>
          </table>
          <div style={{ background: acc, borderRadius: 8, padding: "12px 16px", textAlign: "right" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 4 }}>Totale dovuto</div>
            <div style={{ color: "#fff", fontWeight: "bold", fontSize: 22 }}>€ {fmt(tt)}</div>
          </div>
        </div>
      </div>

      <FooterDoc lyt={lyt} rs={rs} email={email} acc={acc} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// COMMERCIALE
// Logo in box sx · cedente dx · titolo allineato a dx
// Tabella 3 colonne (Descrizione / Importo / % IVA) · pagamento e scadenze affiancati
// Riepilogo IVA e totali in basso · footer con contatti
// ────────────────────────────────────────────────────────────────
function TemplateCommerciale({ lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban }) {
  const { tt } = calcTotali(voci);
  const { imponibile, nonImponibile } = splitImponibile(voci);
  const rp = dense ? "5px 8px" : "8px 8px";
  const ibanVal = iban.replace(/^IBAN:\s*/i, "");
  const contactLine = [sito, email, telefono ? `tel: ${telefono}` : ""].filter(Boolean).join(" - ");

  return (
    <>
      {/* Header: logo box + cedente */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ border: "1px solid #d1d5db", borderRadius: 2, padding: 6, background: "#fff" }}>
          <LogoImg lyt={lyt} w={100} h={52} />
        </div>
        <div style={{ textAlign: "right", fontSize: 10.5, maxWidth: "58%" }}>
          {lyt.mostra_nome_struttura && <div style={{ fontWeight: "bold", fontSize: 12, color: "#111827" }}>{rs}</div>}
          {lyt.mostra_indirizzo && addr && <div style={{ color: "#374151" }}>{addr}</div>}
          {lyt.mostra_piva && pvLine && <div style={{ color: "#374151" }}>{pvLine.replace(/P\.IVA/gi, "P.iva").replace(/C\.F\./gi, "C.F.")}</div>}
        </div>
      </div>

      {/* Titolo documento */}
      <div style={{ textAlign: "right", fontWeight: "bold", fontSize: 12, marginBottom: 14, color: "#111827" }}>
        ANTEPRIMA FATTURA — MAGGIO 2026
      </div>

      {/* CF/Email + Destinatario */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14, fontSize: 10.5 }}>
        <div style={{ flex: "0 0 42%", color: "#374151" }}>
          {dest.cf && <div>CF {dest.cf}</div>}
          {dest.email && <div>EMAIL {dest.email.toLowerCase()}</div>}
        </div>
        <div style={{ flex: 1, textAlign: "right" }}>
          <div style={{ fontSize: 8, color: "#9ca3af", marginBottom: 2, textTransform: "uppercase" }}>Destinatario</div>
          <div style={{ fontWeight: "bold", color: "#111827" }}>{dest.nome}</div>
          {dest.indirizzo && <div style={{ color: "#374151" }}>{dest.indirizzo}</div>}
        </div>
      </div>

      {/* Tabella 3 colonne */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, fontSize: 10.5 }}>
        <thead>
          <tr style={{ background: "#e5e7eb", color: "#111827" }}>
            <th style={{ padding: "6px 8px", textAlign: "left", border: "1px solid #d1d5db", fontWeight: "bold" }}>DESCRIZIONE</th>
            <th style={{ padding: "6px 8px", textAlign: "right", width: "16%", border: "1px solid #d1d5db", fontWeight: "bold" }}>IMPORTO</th>
            <th style={{ padding: "6px 8px", textAlign: "center", width: "10%", border: "1px solid #d1d5db", fontWeight: "bold" }}>% IVA</th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
              <td style={{ padding: rp, border: "1px solid #e5e7eb" }}>{(v.descrizione || "—").toUpperCase()}</td>
              <td style={{ padding: rp, textAlign: "right", border: "1px solid #e5e7eb" }}>€ {fmt((v.quantita || 1) * (v.prezzo_unitario || 0))}</td>
              <td style={{ padding: rp, textAlign: "center", border: "1px solid #e5e7eb" }}>{IVA_COL_NUM[v.regime_iva] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Note */}
      {lyt.blocco_note_automatiche && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 8, color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>Note</div>
          <div style={{ fontSize: 10, color: "#374151", lineHeight: 1.4 }}>
            {lyt.note_automatiche_text || "Copia del documento elettronico disponibile nella Sua area riservata dell'Agenzia delle Entrate"}
          </div>
        </div>
      )}

      {/* Pagamento + Scadenze affiancati */}
      <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 10.5 }}>
        {ibanVal && (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 8, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>Modalità di pagamento</div>
            <div>Bonifico Bancario</div>
            <div>IBAN: {ibanVal}</div>
            <div>Intestato a: {rs}</div>
          </div>
        )}
        {lyt.blocco_scadenze && (
          <div style={{ flex: 1, textAlign: ibanVal ? "right" : "left" }}>
            <div style={{ fontSize: 8, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>Scadenze</div>
            <div style={{ fontWeight: "bold" }}>28/06/2026: € {fmt(tt)}</div>
          </div>
        )}
      </div>

      {/* Riepilogo IVA + totali */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 8 }}>
        {lyt.blocco_riepilogo_iva_dettagliato ? (
          <div style={{ flex: "0 0 58%", fontSize: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#e5e7eb", color: "#111827" }}>
                  <th style={{ padding: "5px 8px", textAlign: "left", border: "1px solid #d1d5db" }}>Riepilogo IVA</th>
                  <th style={{ padding: "5px 8px", textAlign: "right", border: "1px solid #d1d5db" }}>Imponibile</th>
                  <th style={{ padding: "5px 8px", textAlign: "right", border: "1px solid #d1d5db" }}>Imposte</th>
                </tr>
              </thead>
              <tbody>
                {groupIvaDetailed(voci).map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px", border: "1px solid #e5e7eb", fontSize: 9.5 }}>{r.regime}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", border: "1px solid #e5e7eb" }}>€ {fmt(r.imp)}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", border: "1px solid #e5e7eb" }}>€ {fmt(r.iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ flex: 1 }} />}
        <div style={{ flex: "0 0 38%", textAlign: "right", fontSize: 10.5 }}>
          <div style={{ marginBottom: 4 }}><span style={{ color: "#6b7280", marginRight: 12 }}>Imponibile</span>€ {fmt(imponibile)}</div>
          {nonImponibile > 0 && (
            <div style={{ marginBottom: 8 }}><span style={{ color: "#6b7280", marginRight: 12 }}>Non imponibile</span>€ {fmt(nonImponibile)}</div>
          )}
          <div style={{ fontWeight: "bold", fontSize: 18, color: "#111827", marginTop: 6 }}>€ {fmt(tt)}</div>
        </div>
      </div>

      {/* Footer commerciale */}
      <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 16, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 8.5, color: "#9ca3af" }}>
        <span>Anteprima fattura — Maggio 2026 · 1/1</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {contactLine && <span>{contactLine}</span>}
          {lyt.blocco_logo && lyt.logo_base64 && <LogoImg lyt={lyt} w={24} h={12} />}
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// FATTURE IN CLOUD
// Replica del PDF standard Fatture in Cloud (coordinate in pt, scalate alla preview):
// logo intestazione in alto a sinistra · logo piè di pagina in basso a destra
// Scadenze, riepilogo IVA e totale ancorati al fondo pagina · tutto in grigio
// ────────────────────────────────────────────────────────────────
const FIC_W = 538.588;
const FIC_SCALE = { desktop: 640 / FIC_W, print: 687.87 / FIC_W, mobile: 347 / FIC_W };
const FIC_LABEL = "#828282";
const FIC_TEXT = "#6a6a6a";
const FIC_RULE = "#cecece";
const FIC_FILL = "#eeeeee";
const FIC_FONT = "'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', Tahoma, sans-serif";
const FIC_LEGAL = "Copia del documento elettronico disponibile nella Sua area riservata dell'Agenzia delle Entrate";

function ficMoney(v) {
  const [int, dec] = Math.abs(Number(v || 0)).toFixed(2).split(".");
  return `${Number(v) < 0 ? "-" : ""}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`;
}

function ficPx(v, k) {
  return `${(v * k).toFixed(2)}px`;
}

function ficRuns(runs, size, color, k) {
  return runs.map(([text, bold, italic], i) => (
    <span
      key={i}
      style={{
        fontStyle: italic ? "italic" : "normal",
        // FIC simula il grassetto con il contorno del glifo regular
        ...(bold ? { WebkitTextStroke: `${(size * 0.0385 * k).toFixed(2)}px ${color}` } : null),
      }}
    >
      {text}
    </span>
  ));
}

function FicText({ k, x, xr, xc, y, size, color = FIC_TEXT, runs }) {
  const pos = xr !== undefined
    ? { right: ficPx(FIC_W - xr, k) }
    : xc !== undefined
      ? { left: ficPx(xc, k), transform: "translateX(-50%)" }
      : { left: ficPx(x || 0, k) };
  return (
    <span style={{
      position: "absolute", top: ficPx(y - size * 0.85, k), fontSize: ficPx(size, k),
      lineHeight: 1, whiteSpace: "nowrap", color, ...pos,
    }}>
      {ficRuns(runs, size, color, k)}
    </span>
  );
}

function FicRule({ k, y, x1 = 0, x2 = FIC_W, vertical = false, h = 0 }) {
  const line = `${ficPx(0.75, k)} solid ${FIC_RULE}`;
  return vertical
    ? <div style={{ position: "absolute", left: ficPx(x1, k), top: ficPx(y, k), height: ficPx(h, k), borderLeft: line }} />
    : <div style={{ position: "absolute", left: ficPx(x1, k), width: ficPx(x2 - x1, k), top: ficPx(y, k), borderTop: line }} />;
}

function TemplateFic({ lyt, dense, ext, voci, dest, config, mode }) {
  const k = FIC_SCALE[mode] || FIC_SCALE.desktop;
  const px = (v) => ficPx(v, k);
  const rule = `${px(0.75)} solid ${FIC_RULE}`;
  const { ti, tv, tt } = calcTotali(voci);

  const rs = (config.ragione_sociale || "STRUTTURA ESEMPIO S.R.L.").toUpperCase();
  const cityPart = config.citta
    ? (config.provincia ? `${config.citta} (${config.provincia})` : config.citta)
    : (config.provincia ? `(${config.provincia})` : "");
  const sede = ([config.indirizzo, config.cap, cityPart].filter(Boolean).join(" - ") || "Via Esempio 1 - 00100 - Roma (RM)").toUpperCase();
  const pivaLine = [
    config.partita_iva ? `P.iva ${config.partita_iva}` : "",
    config.codice_fiscale ? `C.F. ${config.codice_fiscale}` : "",
  ].filter(Boolean).join(" - ") || "P.iva 01234567890 - C.F. 01234567890";
  const sito = lyt.contatti_sito_web || config.sito_web || "";
  const emailC = lyt.contatti_email || config.email || config.pec || "";
  const tel = lyt.contatti_telefono || config.telefono || "";
  const cell = lyt.contatti_cellulare || config.cellulare || "";

  // ── Intestazione (y dal bordo superiore del logo) ──
  const company = [];
  if (lyt.mostra_nome_struttura) company.push([rs, true]);
  if (lyt.mostra_indirizzo && sede) company.push([sede, false]);
  if (lyt.mostra_piva && pivaLine) company.push([pivaLine, false]);
  if (ext) {
    if (lyt.mostra_telefono && tel) company.push([`tel: ${tel}`, false]);
    if (lyt.mostra_email && emailC) company.push([emailC.toLowerCase(), false]);
    if (lyt.mostra_sito_web && sito) company.push([sito.toLowerCase(), false]);
  }
  const logoScale = Math.max(2, Math.min(8, Number(lyt.logo_larghezza_cm) || 4.5)) / 4.5;
  const logoW = 113.35 * logoScale;
  const logoH = Math.min(75 * logoScale, 88);
  const compY0 = 30.638;
  const titleY = company.length ? Math.max(72.375, compY0 + (company.length - 1) * 9.9 + 21.937) : 72.375;
  const sepY = Math.max(titleY + 24.888, lyt.blocco_logo ? logoH + 22.263 : 0);

  // ── Destinatario (y dal separatore) ──
  const destLeft = [];
  if (dest.cf) destLeft.push([["CF", true], [` ${dest.cf}`, false]]);
  if (dest.email) destLeft.push([["EMAIL", true], [` ${dest.email.toLowerCase()}`, false]]);
  const [addr1, ...addrRest] = (dest.indirizzo || "").split(", ");
  const destRight = [[dest.nome, true], addr1 ? [addr1, false] : null, addrRest.length ? [addrRest.join(", "), false] : null].filter(Boolean);
  const destH = Math.max(
    destLeft.length ? 17.264 + (destLeft.length - 1) * 11.97 : 0,
    26.241 + (destRight.length - 1) * 11.97,
  ) + 40.194;

  // ── Righe ──
  const rowMin = dense ? 19.5 : 23.55;
  const rowPadTop = (rowMin - 9) / 2 + 7.65 - 9.2;
  const rowPadBottom = rowMin - rowPadTop - 11.97;

  // ── Note ──
  const noteParas = [];
  if (lyt.blocco_note_automatiche) {
    (lyt.note_automatiche_text || "").split("\n").map((s) => s.trim()).filter(Boolean)
      .forEach((text) => noteParas.push({ text, size: 9, bold: true }));
  }
  if (config.iban) {
    noteParas.push({ text: "BONIFICO BANCARIO INTESTATO A:", size: 9, bold: true });
    noteParas.push({ text: rs, size: 9.563, bold: true });
    noteParas.push({ text: `IBAN: ${config.iban}`, size: 9.563, bold: true });
    if (config.banca) noteParas.push({ text: config.banca, size: 9.563, bold: true, color: "#000" });
  }
  if (lyt.blocco_note_automatiche) {
    noteParas.push({ text: FIC_LEGAL, size: 9, bold: false, tight: noteParas.length > 0 });
  }
  (lyt.footer_text || "").split("\n").map((s) => s.trim()).filter(Boolean)
    .forEach((text) => noteParas.push({ text, size: 9, bold: false }));

  // ── Riepilogo e totali (y dalla riga Scadenze) ──
  const showScad = !!lyt.blocco_scadenze;
  const o = showScad ? 0 : -75;
  const ivaRows = lyt.blocco_riepilogo_iva_dettagliato
    ? groupIvaDetailed(voci).map((r) => {
        const idx = r.regime.indexOf(" - ");
        const runs = idx > 0
          ? [[r.regime.slice(0, idx + 3), false], [r.regime.slice(idx + 3), false, true]]
          : [[r.regime, false]];
        return { runs, imp: r.imp, iva: r.iva };
      })
    : [{ runs: [["Totale", false]], imp: ti, iva: tv }];
  const totLines = [["Imponibile", ti], ...(tv ? [["IVA", tv]] : [])];

  // ── Piè di pagina (y dalla riga di chiusura riepilogo) ──
  const hasFooterLogo = !!lyt.logo_footer_base64;
  const footerLines = [[[rs, true]]];
  const webMail = [sito.toLowerCase(), emailC.toLowerCase()].filter(Boolean).join(" - ");
  if (webMail) footerLines.push([[webMail, false]]);
  const phones = [tel ? `tel: ${tel}` : "", cell ? `cell: ${cell}` : ""].filter(Boolean).join(" - ");
  if (phones) footerLines.push([[phones, false]]);

  return (
    <div style={{ fontFamily: FIC_FONT, color: FIC_TEXT, display: "flex", flexDirection: "column", minHeight: px(798) }}>
      {/* Intestazione */}
      <div style={{ position: "relative", height: px(sepY), flexShrink: 0 }}>
        {lyt.blocco_logo && (
          <div style={{ position: "absolute", left: px(2.325), top: 0, width: px(logoW), height: px(logoH), display: "flex", alignItems: "flex-start" }}>
            {lyt.logo_base64
              ? <img src={`data:${lyt.logo_content_type || "image/png"};base64,${lyt.logo_base64}`} alt="logo"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", objectPosition: "left top" }} />
              : <LogoPlaceholder w={Math.round(logoW * k)} h={Math.round(logoH * k)} />}
          </div>
        )}
        {company.map(([text, bold], i) => (
          <FicText key={i} k={k} xr={536.263} y={compY0 + i * 9.9} size={8.25} color={FIC_LABEL} runs={[[text, bold]]} />
        ))}
        <FicText k={k} xr={536.263} y={titleY} size={10.5} color={FIC_LABEL}
          runs={[["ANTEPRIMA FATTURA", true], [" — ", false], ["Maggio 2026", true]]} />
        <FicRule k={k} y={sepY} />
      </div>

      {/* CF / Email + Destinatario */}
      <div style={{ position: "relative", height: px(destH), flexShrink: 0 }}>
        {destLeft.map((runs, i) => (
          <FicText key={i} k={k} x={0} y={17.264 + i * 11.97} size={9} runs={runs} />
        ))}
        <FicText k={k} x={283.588} y={14.98} size={6.75} color={FIC_LABEL} runs={[["DESTINATARIO", false]]} />
        {destRight.map(([text, bold], i) => (
          <FicText key={i} k={k} x={283.588} y={26.241 + i * 11.97} size={9} runs={[[text, bold]]} />
        ))}
      </div>

      {/* Tabella Descrizione / Importo */}
      <table style={{ width: "100%", borderCollapse: "collapse", flexShrink: 0 }}>
        <thead>
          <tr style={{ height: px(19.95), borderTop: rule, borderBottom: rule }}>
            <th style={{ padding: `0 0 0 ${px(7.5)}`, textAlign: "left", fontWeight: "normal", fontSize: px(6), lineHeight: 1, color: FIC_LABEL }}>
              {ficRuns([["DESCRIZIONE", true]], 6, FIC_LABEL, k)}
            </th>
            <th style={{ width: px(57.411), padding: 0, textAlign: "center", fontWeight: "normal", fontSize: px(6), lineHeight: 1, color: FIC_LABEL, borderLeft: rule }}>
              {ficRuns([["IMPORTO", true]], 6, FIC_LABEL, k)}
            </th>
          </tr>
        </thead>
        <tbody>
          {voci.map((v, i) => {
            const q = Number(v.quantita || 1);
            const cellStyle = { padding: `${px(rowPadTop)} 0 ${px(rowPadBottom)}`, fontSize: px(9), lineHeight: px(11.97), verticalAlign: "top" };
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? FIC_FILL : "transparent", borderBottom: rule }}>
                <td style={{ ...cellStyle, paddingLeft: px(7.5) }}>
                  <div>{ficRuns([[(v.descrizione || "—").toUpperCase(), true]], 9, FIC_TEXT, k)}</div>
                  {q !== 1 && <div>{`${ficMoney(q).replace(/,00$/, "")} x € ${ficMoney(v.prezzo_unitario || 0)}`}</div>}
                </td>
                <td style={{ ...cellStyle, paddingRight: px(6), textAlign: "right", whiteSpace: "nowrap", borderLeft: rule }}>
                  € {ficMoney(q * (v.prezzo_unitario || 0))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Note */}
      {noteParas.length > 0 && (
        <div style={{ marginTop: px(18.77), flexShrink: 0 }}>
          <div style={{ fontSize: px(6.75), lineHeight: 1, color: FIC_LABEL }}>NOTE</div>
          {noteParas.map((p, i) => (
            <div key={i} style={{
              fontSize: px(p.size), lineHeight: px(p.size * 1.33), color: p.color || FIC_TEXT,
              marginTop: px(i === 0 ? 1.76 : p.tight ? 0 : 10.08),
            }}>
              {ficRuns([[p.text, p.bold]], p.size, p.color || FIC_TEXT, k)}
            </div>
          ))}
        </div>
      )}

      {(lyt.blocco_firma || lyt.blocco_qr_code) && (
        <div style={{ display: "flex", marginTop: px(16), flexShrink: 0 }}>
          <div style={{ width: px(283.588) }}>
            {lyt.blocco_firma && (
              <>
                <div style={{ fontSize: px(6.75), color: FIC_LABEL }}>FIRMA AUTORIZZATA</div>
                <div style={{ width: px(180), height: px(46), borderBottom: rule }} />
                <div style={{ fontSize: px(7.5), color: FIC_LABEL, marginTop: px(3) }}>Il responsabile amministrativo</div>
              </>
            )}
          </div>
          {lyt.blocco_qr_code && (
            <div>
              <div style={{ fontSize: px(6.75), color: FIC_LABEL }}>PAGA ONLINE</div>
              <div style={{ width: px(79.4), height: px(79.4), marginTop: px(4), border: rule, display: "flex", alignItems: "center", justifyContent: "center", fontSize: px(8), color: FIC_LABEL }}>QR</div>
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1, minHeight: px(16) }} />

      {/* Scadenze · Riepilogo IVA · Totale */}
      <div style={{ position: "relative", height: px(187.5 + o), flexShrink: 0 }}>
        {showScad && (
          <>
            <FicRule k={k} y={0} />
            <FicText k={k} x={0} y={19.463} size={6.75} color={FIC_LABEL} runs={[["SCADENZE", false]]} />
            <FicText k={k} x={0} y={29.363} size={8.25} runs={[["28/06/2026:", true], [` € ${ficMoney(tt)}`, false]]} />
          </>
        )}
        <FicRule k={k} y={75 + o} />
        <FicRule k={k} y={187.5 + o} />
        <FicRule k={k} vertical x1={323.153} y={75 + o} h={112.5} />
        <FicText k={k} x={0} y={93.038 + o} size={6.75} color={FIC_LABEL} runs={[["RIEPILOGO IVA", false]]} />
        <FicText k={k} xr={274.513} y={93.038 + o} size={6.75} color={FIC_LABEL} runs={[["IMPONIBILE", false]]} />
        <FicText k={k} xr={311.528} y={93.038 + o} size={6.75} color={FIC_LABEL} runs={[["IMPOSTE", false]]} />
        {ivaRows.slice(0, 7).map((r, i) => (
          <React.Fragment key={i}>
            <FicText k={k} x={0} y={106.313 + o + i * 10.97} size={8.25} runs={r.runs} />
            <FicText k={k} xr={274.513} y={106.313 + o + i * 10.97} size={8.25} runs={[[ficMoney(r.imp), false]]} />
            <FicText k={k} xr={311.528} y={106.313 + o + i * 10.97} size={8.25} runs={[[`€ ${ficMoney(r.iva)}`, false]]} />
          </React.Fragment>
        ))}
        <table style={{
          position: "absolute", right: px(FIC_W - 537.088), top: px(141.563 + o - (totLines.length - 1) * 10.97 - 8.46),
          borderCollapse: "collapse", fontSize: px(8.25), lineHeight: px(10.97), color: FIC_TEXT,
        }}>
          <tbody>
            {totLines.map(([label, val]) => (
              <tr key={label}>
                <td style={{ padding: `0 ${px(12.75)} 0 0`, textAlign: "right" }}>{label}</td>
                <td style={{ padding: 0, textAlign: "right", whiteSpace: "nowrap" }}>€ {ficMoney(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <FicText k={k} xr={FIC_W} y={170.25 + o} size={22.5} color={FIC_LABEL} runs={[[`€ ${ficMoney(tt)}`, true]]} />
      </div>

      {/* Piè di pagina */}
      <div style={{ position: "relative", height: px(52), flexShrink: 0 }}>
        <FicText k={k} x={0} y={38.375} size={7.5} color={FIC_LABEL} runs={[["Anteprima fattura — Maggio 2026 - 1 / 1", false]]} />
        {footerLines.map((runs, i) => (
          <FicText key={i} k={k} xr={hasFooterLogo ? 484.671 : FIC_W} y={38.375 - ((footerLines.length - 1) / 2) * 9 + i * 9}
            size={7.5} color={FIC_LABEL} runs={runs} />
        ))}
        {hasFooterLogo && (
          <div style={{ position: "absolute", left: px(495.921), top: px(20.75), width: px(42.667), height: px(30), display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <img src={`data:${lyt.logo_footer_content_type || "image/png"};base64,${lyt.logo_footer_base64}`} alt="logo piè di pagina"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Componenti condivisi ─────────────────────────────────────────

function BlocchiComuni({ lyt, iban, tt, acc }) {
  return (
    <>
      {lyt.blocco_note_automatiche && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 8.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Note</div>
          <div style={{ fontSize: 10.5, color: "#6b7280", lineHeight: 1.5 }}>
            {lyt.note_automatiche_text || "Copia del documento elettronico disponibile nella Sua area riservata dell'Agenzia delle Entrate."}
          </div>
        </div>
      )}
      {lyt.blocco_scadenze && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 8.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Scadenza</div>
          <div style={{ fontWeight: "bold", fontSize: 12 }}>28/06/2026 &nbsp;—&nbsp; € {fmt(tt)}</div>
        </div>
      )}
      {lyt.blocco_coordinate_bancarie && (
        <div style={{ marginBottom: 10, padding: "8px 10px", background: "#f8fafc", borderLeft: `3px solid ${acc}`, borderRadius: 3 }}>
          <div style={{ fontSize: 8.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Coordinate bancarie</div>
          <div style={{ fontSize: 11, color: "#374151" }}>{iban}</div>
        </div>
      )}
      {lyt.footer_text && (
        <p style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic", marginBottom: 10 }}>{lyt.footer_text}</p>
      )}

      {/* Firma e QR Code affiancati se entrambi attivi, altrimenti singoli */}
      {(lyt.blocco_firma || lyt.blocco_qr_code) && (
        <div style={{ display: "flex", gap: 16, marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6", alignItems: "flex-end" }}>
          {lyt.blocco_firma && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 8.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Firma autorizzata</div>
              <div style={{ borderBottom: "1.5px solid #374151", width: "70%", marginBottom: 3 }} />
              <div style={{ fontSize: 9, color: "#9ca3af" }}>Il responsabile amministrativo</div>
            </div>
          )}
          {lyt.blocco_qr_code && (
            <div style={{ flexShrink: 0, textAlign: "center" }}>
              <div style={{ fontSize: 8.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Paga online</div>
              {/* QR code simbolico */}
              <svg width="46" height="46" viewBox="0 0 46 46" fill="none" style={{ display: "block", margin: "0 auto" }}>
                <rect width="46" height="46" rx="4" fill="#f8fafc" stroke="#e5e7eb" strokeWidth="1"/>
                {/* pattern QR simbolico */}
                <rect x="4" y="4" width="16" height="16" rx="1" fill="#111827"/>
                <rect x="6" y="6" width="12" height="12" rx="0.5" fill="#f8fafc"/>
                <rect x="8" y="8" width="8" height="8" rx="0.5" fill="#111827"/>
                <rect x="26" y="4" width="16" height="16" rx="1" fill="#111827"/>
                <rect x="28" y="6" width="12" height="12" rx="0.5" fill="#f8fafc"/>
                <rect x="30" y="8" width="8" height="8" rx="0.5" fill="#111827"/>
                <rect x="4" y="26" width="16" height="16" rx="1" fill="#111827"/>
                <rect x="6" y="28" width="12" height="12" rx="0.5" fill="#f8fafc"/>
                <rect x="8" y="30" width="8" height="8" rx="0.5" fill="#111827"/>
                {[26,28,30,32,34,36].map((x, i) => (
                  <rect key={i} x={x} y={26 + (i % 3) * 4} width="3" height="3" rx="0.5" fill={i % 2 === 0 ? "#111827" : "#9ca3af"}/>
                ))}
              </svg>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function IvaTable({ lyt, voci, acc }) {
  if (!lyt.blocco_riepilogo_iva_dettagliato) {
    const { ti, tv } = calcTotali(voci);
    return (
      <div style={{ flex: "0 0 55%", fontSize: 10.5 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: acc, color: "#fff" }}>
              <th style={{ padding: "6px 8px", textAlign: "left" }}>Riepilogo IVA</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Imponibile</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Imposta</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "5px 8px" }}>Totale</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>€ {fmt(ti)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>€ {fmt(tv)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  const rows = groupIva(voci);
  return (
    <div style={{ flex: "0 0 55%", fontSize: 10.5 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: acc, color: "#fff" }}>
            <th style={{ padding: "6px 8px", textAlign: "left" }}>Riepilogo IVA</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Imponibile</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Imposta</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "5px 8px" }}>{r.regime}</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>€ {fmt(r.imp)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>€ {fmt(r.iva)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotaliBlock({ ti, tv, tt, acc }) {
  return (
    <div style={{ flex: "0 0 40%", textAlign: "right" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 4, fontSize: 11, color: "#6b7280" }}>
        <span>Imponibile</span><span>€ {fmt(ti)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 6, fontSize: 11, color: "#6b7280" }}>
        <span>Totale IVA</span><span>€ {fmt(tv)}</span>
      </div>
      <div style={{ borderTop: `2px solid ${acc}`, paddingTop: 8, display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "baseline" }}>
        <span style={{ fontSize: 11, color: "#374151", fontWeight: "600" }}>TOTALE</span>
        <span style={{ fontWeight: "bold", fontSize: 20, color: "#111827" }}>€ {fmt(tt)}</span>
      </div>
    </div>
  );
}

function FooterDoc({ lyt, rs, email, acc }) {
  return (
    <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 20, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 9, color: "#9ca3af" }}>
      <span>Anteprima fattura — Maggio 2026 · 1/1</span>
      <span>{rs}{email ? `  ·  ${email}` : ""}</span>
      {lyt.blocco_logo && lyt.logo_base64
        ? <LogoImg lyt={lyt} w={28} h={14} />
        : <span style={{ width: 20 }} />}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────
function AnteprimaFatturaLiveInner({
  layout = {},
  config = {},
  mode = "desktop",
  sampleDestinatario,
  voci: vocesProp,
}) {
  const lyt = useMemo(() => ({
    template: "classico",
    colore_principale: "#6b7280",
    intestazione: "compatta",
    mostra_nome_struttura: true,
    mostra_indirizzo: true,
    mostra_piva: true,
    mostra_email: true,
    mostra_telefono: false,
    mostra_sito_web: false,
    stile_tabella: "leggibile",
    blocco_logo: true,
    blocco_coordinate_bancarie: false,
    blocco_firma: false,
    blocco_note_automatiche: true,
    blocco_scadenze: true,
    blocco_riepilogo_iva_dettagliato: true,
    blocco_timbro: false,
    footer_text: null,
    note_automatiche_text: null,
    logo_base64: null,
    logo_content_type: null,
    logo_larghezza_cm: 4.5,
    ...layout,
  }), [layout]);

  const tmpl = String(lyt.template || "classico").toLowerCase();
  const acc   = lyt.colore_principale || "#6b7280";
  const dense = lyt.stile_tabella === "compatta";
  const ext   = lyt.intestazione === "estesa";

  const voci = vocesProp && vocesProp.length > 0 ? vocesProp : SAMPLE_VOCI;
  const dest = sampleDestinatario || SAMPLE_DEST;

  const rs      = (config.ragione_sociale || "STRUTTURA ESEMPIO S.R.L.").toUpperCase();
  const piva    = config.partita_iva    ? `P.IVA ${config.partita_iva}`    : "P.IVA 01234567890";
  const cf      = config.codice_fiscale ? `C.F. ${config.codice_fiscale}`  : "C.F. 01234567890";
  const pvLine  = [piva, cf].filter(Boolean).join(" — ");
  const addrRaw = [config.indirizzo, config.cap, config.citta, config.provincia ? `(${config.provincia})` : ""].filter(Boolean).join(" ");
  const addr    = (addrRaw || "Via Esempio 1, 00100 Roma (RM)").toUpperCase();
  const telefono = config.telefono  || "+39 06 1234567";
  const email   = config.pec || config.email || "info@struttura-esempio.it";
  const sito    = config.sito_web   || "www.struttura-esempio.it";
  const iban    = config.iban ? `IBAN: ${config.iban}` : "IBAN: IT60A0306909606100000185678";

  const containerStyle = {
    fontFamily: "system-ui, -apple-system, Helvetica, Arial, sans-serif",
    background: "#fff",
    color: "#111827",
    boxShadow: mode !== "print" ? "0 4px 24px rgba(0,0,0,.10)" : "none",
    ...(mode === "mobile"
      ? { width: "375px", minHeight: "667px", padding: "18px 14px", fontSize: "11px" }
      : mode === "print"
      ? { width: "210mm", padding: "18mm 14mm", fontSize: "10pt", boxShadow: "none" }
      : { width: "720px", padding: "32px 40px", fontSize: "12.5px" }),
  };

  const shared = { lyt, acc, dense, ext, voci, dest, rs, pvLine, addr, telefono, email, sito, iban };

  return (
    <div className="keep-light" style={containerStyle}>
      {tmpl === "moderno"   && <TemplateModerno   {...shared} />}
      {tmpl === "sanitario" && <TemplateSanitario {...shared} />}
      {tmpl === "minimal"   && <TemplateMinimal   {...shared} />}
      {tmpl === "premium"   && <TemplatePremium   {...shared} />}
      {tmpl === "commerciale" && <TemplateCommerciale {...shared} />}
      {tmpl === "fic" && <TemplateFic {...shared} config={config} mode={mode} />}
      {(tmpl === "classico" || !["moderno","sanitario","minimal","premium","commerciale","fic"].includes(tmpl)) && <TemplateClassico {...shared} />}
    </div>
  );
}

// React.memo con confronto di default (per riferimento):
// - toggle/colore/template → setLayout crea nuovo oggetto → riferimento cambia → re-render ✓
// - digitazione in noteText/footerText → layout rimane stesso riferimento (debounce non ancora scattato) → nessun re-render ✓
const AnteprimaFatturaLive = memo(AnteprimaFatturaLiveInner);
export default AnteprimaFatturaLive;
